"use client";

import { useCallback, useState } from "react";
import { loadReadMarks, type ReadMarks, saveReadMark } from "@/lib/read-watermark";
import type { VisitorStorage } from "@/lib/visitor-storage";

/**
 * The visitor's local read marks (see `lib/read-watermark`): loaded once from storage, and advanced
 * by `markRead(chatId, lastMessageAt)` the moment a thread is read. The marks win over any unread
 * count the BE (refetch, stream reconnect) reports for a message at or before them.
 */
export function useReadWatermark(opts: { publicKey: string | null; storage: VisitorStorage }): {
  marks: ReadMarks;
  markRead: (chatId: string, lastMessageAt: string | undefined) => void;
} {
  const { publicKey, storage } = opts;
  const [marks, setMarks] = useState<ReadMarks>(() => loadReadMarks(storage, publicKey));
  const markRead = useCallback(
    (chatId: string, lastMessageAt: string | undefined) => {
      const at = Date.parse(lastMessageAt ?? "");
      if (!chatId || Number.isNaN(at)) return;
      saveReadMark(storage, publicKey, chatId, at);
      setMarks((prev) => ((prev[chatId] ?? 0) >= at ? prev : { ...prev, [chatId]: at }));
    },
    [storage, publicKey],
  );
  return { marks, markRead };
}
